import { OPERATOR_GT_EQUALS } from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type { JqlData, JqlInfo } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import type { TypeConfig, DateTimeValue } from '../../common/types.tsx';
import messages from './messages.tsx';
import { isValidTimePeriodFormat } from './time-functions.tsx';

export const WITHIN_THE_LAST_CONFIG: TypeConfig = {
	type: 'within_the_last',
	componentType: 'time_input',
	label: 'labelWithinTheLast',
	hasInput: true,
	getErrorMessage: (intl: IntlShape, value?: DateTimeValue): string | undefined =>
		isValidTimePeriodFormat(value?.value1, value?.value2)
			? undefined
			: intl.formatMessage(messages.validationMessageInvalidPeriodFormat),
	getFormattedValue: (intl: IntlShape, value?: DateTimeValue) => {
		if (value !== undefined) {
			switch (value.value2.toLowerCase()) {
				case 'm':
					return intl.formatMessage(messages.valueWithinTheLastMinutes, {
						dateNumber: value.value1,
					});
				case 'h':
					return intl.formatMessage(messages.valueWithinTheLastHours, {
						dateNumber: value.value1,
					});
				case 'd':
					return intl.formatMessage(messages.valueWithinTheLastDays, {
						dateNumber: value.value1,
					});
				case 'w':
					return intl.formatMessage(messages.valueWithinTheLastWeeks, {
						dateNumber: value.value1,
					});
				default:
					return value.value2;
			}
		}
		return '';
	},
	getJqlData: (dateTimeValue?: DateTimeValue) => {
		if (!dateTimeValue) {
			return undefined;
		}
		const { value1, value2 } = dateTimeValue;

		return {
			primaryClause: {
				operator: OPERATOR_GT_EQUALS,
				operand: `-${value1}${value2}`,
			},
		};
	},
	getValueFromJqlData: (jqlData?: JqlData): DateTimeValue | undefined => {
		if (jqlData !== undefined) {
			const { primaryClause, secondaryClause } = jqlData;
			if (primaryClause !== undefined && secondaryClause !== undefined) {
				// Does not match type if more than one clause is present
				return undefined;
			}
			const clause: JqlInfo | undefined = primaryClause || secondaryClause;
			if (clause && clause.operator === OPERATOR_GT_EQUALS) {
				const value = clause.operand !== undefined ? clause.operand : '';
				if (value) {
					const matches = value.match(/^[-]([0-9]*)([mhdw])$/);
					if (matches?.length === 3) {
						return {
							value1: matches[1],
							value2: matches[2],
						};
					}
				}
			}
			return undefined;
		}
		return undefined;
	},
};
